import { useState } from 'react'

// ── Upgrade paywall bottom sheet ──────────────────────────────────────────
// Shown when a church has used all its free sermon scans. Pitches the full
// archive import and collects an enquiry, which is emailed to the team via the
// submit-church-enquiry edge function (type: 'upgrade_request').
//
// Props:
//   church   — { id, name }
//   profile  — { full_name, email } (used to pre-fill the form)
//   email    — auth email fallback when profile has none
//   scansUsed — number, included in the team notification
//   title    — optional override for the heading
//   onClose  — close the sheet

const ACCENT = 'var(--gold)'
const NAVY   = 'var(--ink)'
const SERIF  = "'Source Serif 4', Georgia, serif"
const SANS   = "'Plus Jakarta Sans', system-ui, sans-serif"

export default function UpgradeSheet({ church, profile, email, scansUsed = 0, title, onClose }) {
  const [name,     setName]     = useState(profile?.full_name || '')
  const [mail,     setMail]     = useState(profile?.email || email || '')
  const [churchNm, setChurchNm] = useState(church?.name || '')
  const [message,  setMessage]  = useState('')
  const [sending,  setSending]  = useState(false)
  const [error,    setError]    = useState('')
  const [done,     setDone]     = useState(false)

  async function submit(e) {
    e?.preventDefault?.()
    if (sending) return
    setSending(true); setError('')
    try {
      const ANON = process.env.REACT_APP_SUPABASE_ANON_KEY
      const base = process.env.REACT_APP_SUPABASE_URL
      const res = await fetch(`${base}/functions/v1/submit-church-enquiry`, {
        method: 'POST',
        headers: {
          'Content-Type':  'application/json',
          'Authorization': `Bearer ${ANON}`,
          'apikey':        ANON,
        },
        body: JSON.stringify({
          type:       'upgrade_request',
          name:       name.trim(),
          email:      mail.trim(),
          church:     churchNm.trim(),
          message:    message.trim(),
          scans_used: scansUsed,
          church_id:  church?.id || '',
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || data?.error) throw new Error(data?.error || `HTTP ${res.status}`)
      setDone(true)
    } catch (err) {
      setError(err?.message || 'Something went wrong. Please try again.')
    } finally {
      setSending(false)
    }
  }

  const s = {
    backdrop: { position:'fixed', inset:0, zIndex:10000, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'flex-end', justifyContent:'center', fontFamily:SANS },
    sheet:    { width:'100%', maxWidth:520, background:'var(--white)', borderTopLeftRadius:20, borderTopRightRadius:20, border:'1px solid var(--border-strong)', borderBottom:'none', maxHeight:'92vh', overflowY:'auto', padding:'10px 20px calc(env(safe-area-inset-bottom) + 22px)', boxShadow:'0 -20px 60px rgba(0,0,0,0.25)' },
    grab:     { width:40, height:4, borderRadius:2, background:'rgba(31,27,22,0.18)', margin:'8px auto 16px' },
    title:    { fontFamily:SERIF, fontSize:26, color:'var(--ink)', lineHeight:1.15, marginBottom:10 },
    body:     { fontSize:14, color:'var(--stone)', lineHeight:1.55, marginBottom:18 },
    goldBox:  { background:'rgba(232,200,122,0.08)', border:'1px solid rgba(232,200,122,0.28)', borderRadius:14, padding:'16px 16px', marginBottom:20 },
    goldHead: { fontSize:15, fontWeight:800, color:'var(--gold-ink)', marginBottom:10 },
    feat:     { display:'flex', gap:9, alignItems:'flex-start', fontSize:13.5, color:'var(--ink)', lineHeight:1.5, marginBottom:7 },
    label:    { fontSize:11, fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', color:'var(--stone)', margin:'12px 0 6px', display:'block' },
    input:    { width:'100%', background:'var(--white)', border:'1px solid var(--border-strong)', borderRadius:10, padding:'12px 14px', color:'var(--ink)', fontSize:15, fontFamily:SANS, boxSizing:'border-box', outline:'none' },
    textarea: { width:'100%', background:'var(--white)', border:'1px solid var(--border-strong)', borderRadius:10, padding:'12px 14px', color:'var(--ink)', fontSize:15, fontFamily:SANS, boxSizing:'border-box', outline:'none', minHeight:78, resize:'vertical' },
    primary:  { width:'100%', background:ACCENT, color:NAVY, border:'none', borderRadius:12, padding:'15px', fontSize:15, fontWeight:800, cursor:'pointer', marginTop:18, fontFamily:SANS },
    dis:      { opacity:0.5, cursor:'default' },
    cancel:   { width:'100%', background:'none', border:'none', color:'var(--stone)', fontSize:13, fontWeight:600, cursor:'pointer', marginTop:10, padding:8, fontFamily:SANS },
    err:      { background:'rgba(249,112,102,0.12)', border:'1px solid rgba(249,112,102,0.3)', color:'#C8372A', borderRadius:10, padding:'10px 12px', fontSize:13, marginTop:12 },
  }

  // Tap the backdrop (not the sheet) to dismiss.
  function onBackdrop(e) { if (e.target === e.currentTarget) onClose?.() }

  return (
    <div style={s.backdrop} onClick={onBackdrop}>
      <style>{`@keyframes us-up { from { transform: translateY(100%); } to { transform:none; } }`}</style>
      <div style={{ ...s.sheet, animation:'us-up 280ms cubic-bezier(0.16,1,0.3,1)' }}>
        <div style={s.grab}/>

        {done ? (
          <div style={{ textAlign:'center', padding:'18px 6px 10px' }}>
            <div style={{ fontSize:44, marginBottom:14 }}>🙌</div>
            <div style={{ ...s.title, fontSize:24 }}>We'll be in touch today</div>
            <div style={s.body}>Check your email — we've sent a confirmation and we'll follow up shortly to get your full archive imported.</div>
            <button type="button" style={s.primary} onClick={() => onClose?.()}>Done</button>
          </div>
        ) : (
          <>
            <div style={s.title}>{title || "You've used your 5 free scans"}</div>
            <div style={s.body}>
              Your free scans have been used. Get in touch and we'll have your full sermon archive imported same day.
            </div>

            <div style={s.goldBox}>
              <div style={s.goldHead}>📖 Import your entire sermon archive</div>
              <div style={s.feat}><span style={{ color:'var(--gold-ink)' }}>✓</span><span>Years of sermons linked to every Bible verse</span></div>
              <div style={s.feat}><span style={{ color:'var(--gold-ink)' }}>✓</span><span>Automatic weekly sermon scanning</span></div>
              <div style={s.feat}><span style={{ color:'var(--gold-ink)' }}>✓</span><span>Unlimited members</span></div>
            </div>

            <form onSubmit={submit}>
              <label style={s.label}>Your name</label>
              <input style={s.input} value={name} onChange={e => setName(e.target.value)} placeholder="Your name"/>

              <label style={s.label}>Email</label>
              <input style={s.input} type="email" value={mail} onChange={e => setMail(e.target.value)} placeholder="you@church.org"/>

              <label style={s.label}>Church name</label>
              <input style={s.input} value={churchNm} onChange={e => setChurchNm(e.target.value)} placeholder="Your church"/>

              <label style={s.label}>How many sermons do you have approximately?</label>
              <textarea style={s.textarea} value={message} onChange={e => setMessage(e.target.value)} placeholder="e.g. 200 videos going back to 2018"/>

              {error && <div style={s.err}>{error}</div>}

              <button
                type="submit"
                disabled={sending || !name.trim() || !mail.trim() || !churchNm.trim()}
                style={{ ...s.primary, ...((sending || !name.trim() || !mail.trim() || !churchNm.trim()) ? s.dis : {}) }}
              >
                {sending ? 'Sending…' : 'Get in touch →'}
              </button>
              <button type="button" style={s.cancel} onClick={() => onClose?.()}>Not now</button>
            </form>
          </>
        )}
      </div>
    </div>
  )
}
